import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  UploadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Card, Col, Input, message, Modal, Row, Select, Button } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { Fragment, useState } from "react";
import { isArrayAndHasValue } from "../../../utils/functions";
import "./custom_input.css";

const attachmentTypes = [
  {
    id: 1,
    name: "Sample",
  },
  {
    id: 2,
    name: "Tech Pack",
  },
  {
    id: 3,
    name: "Sketch",
  },
  {
    id: 5,
    name: "Mail",
  },
  {
    id: 6,
    name: "Link & Mail",
  },
  {
    id: 4,
    name: "Other",
  },
];

export default function DocumentUpload(props) {
  // Props
  const {
    DOC_ADD,
    filesList,
    setFileList,
    view,
    header,
    preview,
    showType,
    showReference,
    singleFile,
    excludeSample,
    showLink,
  } = props;

  // States
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  // Constants
  const api_base_url = process.env.REACT_APP_BASE_URL;

  // Antd
  const { confirm } = Modal;
  const { Option } = Select;

  const addToFilesList = (newFileObject) => {
    let filesListCopy = isArrayAndHasValue(filesList) ? [...filesList] : [];

    const newData = {
      id: newFileObject.id,
      name: newFileObject.document_name,
      path: newFileObject.document,
      is_delete: 0,
    };

    if (singleFile) {
      // If supports only single file
      filesListCopy = [newData];
    } else {
      // If supports multiple files
      filesListCopy.push(newData);
    }

    setFileList(filesListCopy);
  };

  const uploadFile = async (file) => {
    if (!file || typeof file.name !== "string") {
      message.error("No file selected or file is invalid.");
      setLoading(false);
      return;
    }
    setLoading(true);

    // Check file size (15MB = 15 * 1024 * 1024 bytes)
    const maxFileSize = 15 * 1024 * 1024;
    if (file.size > maxFileSize) {
      setLoading(false);
      message.error("File size exceeds 15MB. Please upload a smaller file.");
      return;
    }

    // #,%,$,@,!,^,&,*,"<,>,?,/,\,|,`,~
    const notAllowedFilesName = /[#,%,$,@,!,^,&,*,"<,>,?,/,\\,|,`,~]/;
    if (notAllowedFilesName.test(file.name)) {
      setLoading(false);
      message.error(
        'File name should not contain special characters like #, %, $, @, !, ^, &, *, ", <, >, ?, /, \\, |, `, ~',
      );
      return;
    }
    const URL = process.env.REACT_APP_BASE_URL + DOC_ADD;
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    fetch(URL, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setLoading(false);
        addToFilesList(result.data);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error.message || "Error uploading file!");
      });
  };

  const removeFile = (id) => {
    confirm({
      title: "Do you Want to delete these items?",
      icon: <ExclamationCircleOutlined />,
      onOk() {
        const filesListCopy = [...filesList];
        const foundFile = filesListCopy.find((file) => file.id === id);
        foundFile["is_delete"] = 1;
        setFileList([...filesListCopy]);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const onChangeAttachmentType = (value, id, context) => {
    const filesListCopy = [...filesList];
    const foundItem = filesListCopy.find((item) => item.id === id);
    const foundType = attachmentTypes.find((type) => type.id === value);
    foundItem[context] = foundType?.name;
    setFileList([...filesListCopy]);
  };

  // Filter attachment types based on excludeSample prop
  const filteredAttachmentTypes = excludeSample
    ? attachmentTypes.filter((type) => type.name !== "Sample")
    : attachmentTypes;

  const onChangeFileData = (value, id, context) => {
    const filesListCopy = [...filesList];
    const foundItem = filesListCopy.find((item) => item.id === id);
    foundItem[context] = value;
    setFileList([...filesListCopy]);
  };

  const saveLinkAndMailRow = async (file) => {
    try {
      if (!file?.link) {
        message.error("Please enter document link");
        return;
      }

      if (!file?.attachment_type) {
        message.error("Please select attachment type");
        return;
      }

      setSavingId(file?.id);
      const token = localStorage.getItem("token");
      const URL = api_base_url + DOC_ADD;

      const payload = {
        file: null,
        name: file?.attachment_type,
        link: file?.link,
        attachment_type: file?.attachment_type,
        reference_no: file?.reference_no || null,
        mail_body: file?.attachment_type === "Link & Mail" ? file?.mail_body || null : null,
      };

      const response = await fetch(URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }).then((res) => res.json());

      if (response?.code === 200 && response?.data) {
        message.success("Link & Mail saved successfully");
        // Update the file with the response data
        const filesListCopy = [...filesList];
        const foundItem = filesListCopy.find((item) => item.id === file?.id);
        if (foundItem) {
          foundItem.id = response?.data?.id || foundItem?.id;
          foundItem.name = response?.data?.document_name || foundItem?.name;
          foundItem.document_name = response?.data?.document_name || foundItem?.document_name;
          foundItem.path = response?.data?.document || foundItem?.path;
          foundItem.document = response?.data?.document || foundItem?.document;
          foundItem.link = response?.data?.link || foundItem?.link;
          foundItem.saved = true;
        }
        setFileList([...filesListCopy]);
      } else {
        message.error(response?.message || "Failed to save link & mail");
      }
    } catch (error) {
      message.error(error.message || "Error saving link & mail");
    } finally {
      setSavingId(null);
    }
  };

  const detectFileType = (file) => {
    // Detect file type from extension at end of the file name
    const fileName = file?.path || file?.document;
    if (!fileName || typeof fileName !== "string") {
      return null;
    }
    const extension = fileName.split(".").pop();
    const fileType = extension?.toLowerCase();
    return fileType || null;
  };

  const isImage = (fileType) => {
    if (fileType === "png" || fileType === "jpg" || fileType === "jpeg") {
      return true;
    } else {
      return false;
    }
  };

  const getPreviewUrl = (file) => {
    const filePath = file?.path || file?.document || file?.link;
    if (filePath) {
      if (file?.attachment_type === "Link & Mail") {
        return filePath;
      }
      if (/^https?:\/\//i.test(filePath)) {
        return filePath;
      }
      return `${api_base_url}/${filePath}`;
    }
    return null;
  };

  return (
    <Card className="custome-table" title={<span style={{ fontSize: "14px" }}>{header ? header : "Documents"}</span>}>
      <style>
        {`
          .document-upload-table {
            table-layout: auto !important;
          }
          .document-upload-table th,
          .document-upload-table td {
            white-space: nowrap;
          }
          .document-upload-table .filename-cell {
            white-space: normal;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        `}
      </style>
      <Row gutter={6}>
        <Col span={24}>
          <div className="custome-table w-full" style={{ width: "100%", overflowX: "auto" }}>
            <table className="document-upload-table" style={{ width: "100%", minWidth: "600px" }}>
              <thead>
                <tr>
                  <th className="t-header bc-java" style={{ width: "50px", minWidth: "50px" }}>
                    Sl
                  </th>
                  {showType && (
                    <th className="t-header bc-java" style={{ width: "150px", minWidth: "150px" }}>
                      Attachment Type
                    </th>
                  )}
                  <th className="t-header bc-java" style={{ width: "200px", minWidth: "200px" }}>
                    File Name
                  </th>
                  {showReference && (
                    <th className="t-header bc-java" style={{ width: "150px", minWidth: "150px" }}>
                      Reference No.
                    </th>
                  )}
                  {showLink && (
                    <th className="t-header bc-java" style={{ width: "200px", minWidth: "200px" }}>
                      Link
                    </th>
                  )}
                  {showLink && (
                    <th className="t-header bc-java" style={{ width: "200px", minWidth: "200px" }}>
                      Mail Body
                    </th>
                  )}
                  <th className="t-header bc-java" style={{ width: "80px", minWidth: "80px" }}>
                    File
                  </th>
                  {!view && (
                    <th className="t-header bc-java" style={{ width: "100px", minWidth: "100px" }}>
                      Action
                    </th>
                  )}
                  {preview && (
                    <th className="t-header bc-java" style={{ width: "80px", minWidth: "80px" }}>
                      Preview
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {isArrayAndHasValue(filesList) ? (
                  <>
                    {filesList
                      ?.filter((item) => !item?.is_delete)
                      ?.map((file, index) => (
                        <Fragment key={file?.id}>
                          <tr>
                            <td className="t-body" style={{ width: "50px", minWidth: "50px" }}>
                              {index + 1}
                            </td>
                            {showType && (
                              <td className="t-body" style={{ width: "150px", minWidth: "150px" }}>
                                <Select
                                  showSearch
                                  placeholder="Select type"
                                  optionFilterProp="children"
                                  disabled={view}
                                  size="small"
                                  allowClear
                                  onSelect={(value) => onChangeAttachmentType(value, file?.id, "attachment_type")}
                                  value={file?.attachment_type || null}
                                  style={{ width: "100%", maxWidth: "150px" }}
                                >
                                  {isArrayAndHasValue(filteredAttachmentTypes) &&
                                    filteredAttachmentTypes?.map((typeItem, index) => (
                                      <Option value={typeItem?.id} key={typeItem?.id || index}>
                                        {typeItem?.name || ""}
                                      </Option>
                                    ))}
                                </Select>
                              </td>
                            )}
                            <td
                              className="t-body filename-cell"
                              style={{
                                width: "200px",
                                minWidth: "200px",
                                maxWidth: "200px",
                                textAlign: "left",
                                padding: "4px 8px",
                              }}
                            >
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                }}
                                title={file.name || file.document_name || ""}
                              >
                                {file.name || file.document_name || ""}
                              </span>
                            </td>
                            {showReference && (
                              <td className="t-body" style={{ width: "150px", minWidth: "150px" }}>
                                <Input
                                  placeholder="Reference No."
                                  size="small"
                                  disabled={view}
                                  onChange={(e) => onChangeFileData(e.target.value, file?.id, "reference_no")}
                                  value={file?.reference_no || null}
                                  style={{ width: "100%", maxWidth: "150px" }}
                                />
                              </td>
                            )}
                            {showLink && file?.attachment_type === "Link & Mail" && (
                              <td className="t-body" style={{ width: "200px", minWidth: "200px" }}>
                                <Input
                                  placeholder="Document Link"
                                  size="small"
                                  disabled={view}
                                  onChange={(e) => onChangeFileData(e.target.value, file?.id, "link")}
                                  value={file?.link || null}
                                  style={{ width: "100%", maxWidth: "200px" }}
                                />
                              </td>
                            )}
                            {showLink && file?.attachment_type !== "Link & Mail" && (
                              <td className="t-body" style={{ width: "200px", minWidth: "200px" }}>
                                -
                              </td>
                            )}
                            {showLink && file?.attachment_type === "Link & Mail" && (
                              <td className="t-body" style={{ width: "200px", minWidth: "200px" }}>
                                <TextArea
                                  placeholder="Mail Body"
                                  size="small"
                                  disabled={view}
                                  onChange={(e) => onChangeFileData(e.target.value, file?.id, "mail_body")}
                                  value={file?.mail_body || null}
                                  style={{ width: "100%", maxWidth: "200px" }}
                                  rows={2}
                                />
                              </td>
                            )}
                            {showLink && file?.attachment_type !== "Link & Mail" && (
                              <td className="t-body" style={{ width: "200px", minWidth: "200px" }}>
                                -
                              </td>
                            )}
                            <td className="t-body" style={{ width: "80px", minWidth: "80px" }}>
                              {(() => {
                                const isLinkAndMail = file?.attachment_type === "Link & Mail";
                                const previewUrl = getPreviewUrl(file);
                                if (isLinkAndMail && !previewUrl) {
                                  return <span style={{ color: "#ccc" }}>-</span>;
                                }
                                if (!previewUrl) {
                                  return <span style={{ color: "#ccc" }}>-</span>;
                                }
                                return (
                                  <span onClick={() => window.open(previewUrl, "_blank")} style={{ cursor: "pointer" }}>
                                    <FilePdfOutlined style={{ fontSize: "16px" }} />
                                  </span>
                                );
                              })()}
                            </td>
                            {!view && (
                              <td className="t-body" style={{ width: "100px", minWidth: "100px" }}>
                                {file?.attachment_type === "Link & Mail" ? (
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={savingId === file?.id ? <LoadingOutlined /> : <SaveOutlined />}
                                    loading={savingId === file?.id}
                                    onClick={() => saveLinkAndMailRow(file)}
                                    disabled={savingId === file?.id}
                                    style={{ fontSize: "12px", padding: "4px 8px" }}
                                  >
                                    Save
                                  </Button>
                                ) : (
                                  <DeleteOutlined
                                    style={{ fontSize: "16px", color: "red" }}
                                    className="cursore-pointer"
                                    onClick={() => removeFile(file?.id)}
                                  />
                                )}
                              </td>
                            )}
                            {preview && (
                              <td className="t-body" style={{ width: "80px", minWidth: "80px" }}>
                                {isImage(detectFileType(file)) ? (
                                  <span style={{ cursor: "pointer" }}>
                                    <EyeOutlined
                                      style={{
                                        fontSize: "16px",
                                        color: "#2980B9",
                                      }}
                                    />
                                  </span>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        </Fragment>
                      ))}
                  </>
                ) : (
                  ""
                )}

                <tr>
                  <td
                    className="t-body"
                    colSpan={
                      3 + // Sl, File Name, File (always present)
                      (showType ? 1 : 0) +
                      (showReference ? 1 : 0) +
                      (showLink ? 2 : 0) +
                      (!view ? 1 : 0) +
                      (preview ? 1 : 0)
                    }
                  >
                    {!view && (
                      <>
                        <input
                          type="file"
                          id={`file-input-${DOC_ADD}`}
                          hidden
                          onChange={(e) => {
                            uploadFile(e.target.files[0]);
                          }}
                          style={{ fontSize: "16px" }}
                          multiple
                        />
                        <label for={`file-input-${DOC_ADD}`} className="file-input-label">
                          {loading ? <LoadingOutlined /> : <UploadOutlined style={{ fontSize: "14px" }} />}
                          <span style={{ fontSize: "12px" }}>{` Upload`}</span>
                        </label>
                        {showLink && (
                          <Button
                            type="dashed"
                            style={{ marginLeft: "10px", fontSize: "12px" }}
                            onClick={() => {
                              const newLinkEntry = {
                                id: Date.now(), // Temp ID for new rows
                                name: "Link & Mail Entry",
                                path: null,
                                link: null,
                                attachment_type: "Link & Mail",
                                reference_no: null,
                                mail_body: null,
                                is_delete: 0,
                                document_type: "link",
                              };
                              const filesListCopy = isArrayAndHasValue(filesList) ? [...filesList] : [];
                              filesListCopy.push(newLinkEntry);
                              setFileList(filesListCopy);
                            }}
                          >
                            + Add Link & Mail Row
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </Card>
  );
}
